import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowRight, Eye, EyeOff, Shield } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const GOLD = '#C9A961';
const ORANGE = '#FF5E00';

/**
 * Scholar Athlete Academy — Student Login.
 *
 * A single-purpose, brutalist login form. No marketing copy, no enrollment funnel.
 * Routes all academy/scholar-athlete students into /academy/school (the real
 * CA-standards-aligned dashboard). L'Chaim-only accounts still flow to their
 * dedicated dashboard.
 */
const AcademyLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // If already authenticated, route immediately.
  useEffect(() => {
    const token = localStorage.getItem('academy_token');
    const userStr = localStorage.getItem('academy_user');
    if (!token || !userStr) return;
    try {
      const user = JSON.parse(userStr);
      navigate(resolveDestination(user), { replace: true });
    } catch (_) {}
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Email and password are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/academy/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      const { token, user } = res.data;
      localStorage.setItem('academy_token', token);
      localStorage.setItem('academy_user', JSON.stringify(user));
      toast.success(`Welcome back, ${user.name?.split(' ')[0] || 'Scholar'}`);
      navigate(resolveDestination(user), { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Login failed. Check your credentials.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12" data-testid="academy-login-page">
      <div className="w-full max-w-[420px]">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/academy" className="inline-block mb-6" data-testid="academy-login-logo">
            <img
              src="/images/scholar-athlete-logo-new.png"
              alt="Scholar Athlete Academy"
              className="mx-auto h-20 w-auto"
              style={{ filter: `drop-shadow(0 0 40px rgba(255,94,0,0.25))` }}
            />
          </Link>
          <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
            <Shield className="w-3 h-3" style={{ color: GOLD }} />
            <span className="text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
              Student Portal
            </span>
          </div>
          <h1 className="mt-6 text-3xl font-black uppercase tracking-tight">Sign In</h1>
          <p className="text-xs text-gray-500 tracking-widest uppercase mt-2">
            Continue Your Curriculum
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="academy-login-form">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-2">
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black border-2 border-gray-800 px-4 py-4 text-white font-mono text-sm focus:border-orange-500 outline-none transition-colors"
              placeholder="you@school.edu"
              data-testid="academy-login-email"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500">
                Password
              </label>
              <Link
                to="/academy/forgot-password"
                className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500 hover:text-orange-400"
                data-testid="academy-login-forgot-link"
              >
                Forgot?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black border-2 border-gray-800 px-4 py-4 pr-12 text-white font-mono text-sm focus:border-orange-500 outline-none transition-colors"
                placeholder="••••••••"
                data-testid="academy-login-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-orange-500"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-gray-800 disabled:text-gray-600 text-black py-5 font-black text-sm tracking-[0.2em] uppercase transition-colors flex items-center justify-center gap-2"
            data-testid="academy-login-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Signing In
              </>
            ) : (
              <>
                Enter Academy <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        {/* Footer links */}
        <div className="mt-10 text-center space-y-3">
          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <p className="text-[11px] uppercase tracking-[0.25em] text-gray-600">
            New here?{' '}
            <Link
              to="/academy/enroll"
              className="text-orange-500 hover:text-orange-400 font-bold"
              data-testid="academy-login-enroll-link"
            >
              Enroll →
            </Link>
          </p>
          <Link
            to="/academy"
            className="block text-[10px] uppercase tracking-[0.3em] text-gray-700 hover:text-gray-400"
          >
            ← Back to Academy
          </Link>
        </div>
      </div>
    </div>
  );
};

/**
 * Decide where to send a freshly-logged-in user.
 * L'Chaim-ONLY users keep their dedicated dashboard. Everyone else lands inside
 * the real California-standards school dashboard.
 */
function resolveDestination(user) {
  if (!user) return '/academy/school';
  if (user.role === 'teacher') return '/teacher-dashboard';
  const isLChaimOnly = user.lchaim_tier && !user.is_student && !user.subscription_tier;
  if (isLChaimOnly) return '/academy/lchaim/dashboard';
  return '/academy/school';
}

export default AcademyLogin;
